// ============================================================================
// The review a builder is handed when QuoteMaker writes a quote (Peter,
// 2026-11-01: the builder is taken to the Assistant when QuoteMaker writes a
// quote, to make the final edits at Stages 4 and 5 before it goes out).
// ----------------------------------------------------------------------------
// Built from QuoteMaker's own record on the quote (quoteData.meta.quoteBrain):
// what it asks the builder to confirm, what it left to fix and what it left to
// check. No AI call (Peter's choice, 2026-11-01). Each point names its item,
// so the review card can ask the Assistant about it.
//   - One review per written version (its run): a Write again that is adopted
//     brings a new one.
//   - This device remembers, for each version, the stages its review brought
//     the Assistant forward at (once at Stage 4 and once at Stage 5) and
//     whether the builder closed it, which ends it.
// ============================================================================

import { builderText } from '../../backend/src/shared/quote-brain/builder-text.js';

type Record_ = Record<string, any>;

export interface QuoteMakerReviewPoint {
    /** The point in the builder's words, its item named first ("Item 3: ..."). */
    text: string;
    /** 1-based position of its item on the quote, or null when it is about the whole quote. */
    itemNumber: number | null;
}

export interface QuoteMakerReviewPoints {
    fix: QuoteMakerReviewPoint[];
    confirm: QuoteMakerReviewPoint[];
    check: QuoteMakerReviewPoint[];
}

export interface QuoteMakerReview extends QuoteMakerReviewPoints {
    runId: string;
    writtenAt: string | null;
    itemCount: number;
}

function itemNumbers(scope: Record_[]): Map<string, number> {
    const numbers = new Map<string, number>();
    scope.forEach((row, index) => {
        const id = row?.quoteBrain?.itemId;
        if (id && !numbers.has(id)) numbers.set(id, index + 1);
    });
    return numbers;
}

/**
 * QuoteMaker's own findings and questions on its quote, each with its item. Null for a quote QuoteMaker did not
 * write. The scope is the quote's own when it has one, else the list on screen.
 */
export function quoteMakerReviewPoints(quote: Record_ | null | undefined, scope: Record_[] = []): QuoteMakerReviewPoints | null {
    const meta = quote?.meta?.quoteBrain;
    if (!meta?.runId) return null;
    const numbers = itemNumbers(Array.isArray(quote?.scope) && quote!.scope.length ? quote!.scope : scope);
    const words = (t: unknown) => builderText(String(t || ''), { itemLabel: (id: string) => (numbers.has(id) ? `Item ${numbers.get(id)}` : '') });
    const point = (itemId: unknown, text: string): QuoteMakerReviewPoint => {
        const n = itemId ? numbers.get(String(itemId)) ?? null : null;
        return { text: `${n ? `Item ${n}: ` : ''}${text}`, itemNumber: n };
    };
    const findings = Array.isArray(meta.findings) ? meta.findings : [];
    const confirms = Array.isArray(meta.confirms) ? meta.confirms : [];
    return {
        fix: findings.filter((f: Record_) => f?.blocking).map((f: Record_) => point(f.itemId, words(f.message))),
        confirm: confirms.map((c: Record_) => point(c.itemId, `${words(c.question)}${c.assumption ? ` (the quote assumes: ${words(c.assumption)})` : ''}`)),
        check: findings.filter((f: Record_) => !f?.blocking).map((f: Record_) => point(f.itemId, words(f.message))),
    };
}

/** The review of a quote QuoteMaker wrote, or null for any other quote. */
export function quoteMakerReviewOf(quote: Record_ | null | undefined, scope: Record_[] = []): QuoteMakerReview | null {
    const points = quoteMakerReviewPoints(quote, scope);
    if (!points) return null;
    const meta = quote!.meta.quoteBrain;
    const rows = Array.isArray(scope) && scope.length ? scope : (Array.isArray(quote?.scope) ? quote!.scope : []);
    return {
        runId: String(meta.runId),
        writtenAt: typeof meta.writtenAt === 'string' ? meta.writtenAt : null,
        itemCount: rows.length,
        ...points,
    };
}

/** What the Assistant is asked when the builder picks a point on the review card. */
export function quoteMakerReviewPrompt(point: QuoteMakerReviewPoint, kind: keyof QuoteMakerReviewPoints): string {
    const ask = kind === 'confirm'
        ? 'QuoteMaker asks me to confirm this. Explain what it means for the quote and what I should change if the assumption is wrong.'
        : kind === 'fix'
            ? 'QuoteMaker left this to fix before the quote is sent. Explain it and propose the fix.'
            : 'QuoteMaker left this to check. Explain it and say whether the quote needs a change.';
    return `${point.text}\n\n${ask}`;
}

export const QUOTEMAKER_REVIEW_READY_PROMPT = 'Check this quote is ready to send to the client, and list anything I should change first.';

// ── This device's record of each review ─────────────────────────────────────

const LEDGER_KEY = 'qm.quoteMakerReview.v1';
const LEDGER_MAX = 100;

/** A version's review on this device: the stages it brought the Assistant forward at, and whether it was closed. */
export interface QuoteMakerReviewEntry {
    raised: Array<4 | 5>;
    done?: boolean;
}
export type QuoteMakerReviewLedger = Record<string, QuoteMakerReviewEntry>;

export function readQuoteMakerReviewLedger(): QuoteMakerReviewLedger {
    try {
        const parsed = JSON.parse(localStorage.getItem(LEDGER_KEY) || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

/** True when the version's review should bring the Assistant forward at this stage. */
export function quoteMakerReviewToRaise(ledger: QuoteMakerReviewLedger, runId: string, stage: 4 | 5): boolean {
    const entry = ledger[runId];
    return !entry?.done && !(Array.isArray(entry?.raised) && entry!.raised.includes(stage));
}

/** Records that the review was raised at a stage, or closed (`'done'`); closed stays closed. Keeps the newest entries only. */
export function markQuoteMakerReview(runId: string, event: 4 | 5 | 'done'): QuoteMakerReviewLedger {
    const ledger = readQuoteMakerReviewLedger();
    const previous = ledger[runId];
    const raised = Array.isArray(previous?.raised) ? previous!.raised : [];
    const entry: QuoteMakerReviewEntry = event === 'done'
        ? { raised, done: true }
        : { raised: raised.includes(event) ? raised : [...raised, event], ...(previous?.done ? { done: true } : {}) };
    const { [runId]: _previous, ...rest } = ledger;
    const next = Object.fromEntries([...Object.entries(rest), [runId, entry] as const].slice(-LEDGER_MAX)) as QuoteMakerReviewLedger;
    try { localStorage.setItem(LEDGER_KEY, JSON.stringify(next)); } catch { /* a per-device convenience only */ }
    return next;
}
